import React, { useState } from 'react';
import {
    Button,
    ButtonGroup,
    Grid2,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useGetVenues } from '@openapi/venues/venues';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import VenuesCardView from './components/VenuesCardView';
import VenuesTableView from './components/VenuesTableView';
import SearchInput from '../../components/atom/SearchInput';
import AddIcon from '@mui/icons-material/Add';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../hooks/useCapabilities';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const VIEW_STORAGE_KEY = 'venues_view';
const VIEW_OPTIONS = ['card', 'table'];

const getStoredView = () => {
    try {
        const stored = localStorage.getItem(VIEW_STORAGE_KEY);
        return VIEW_OPTIONS.includes(stored) ? stored : 'card';
    } catch {
        return 'card';
    }
};

const VenuesPage = () => {
    const { can, CAPABILITIES } = useCapabilities();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { notifyError } = useNotify();
    const [{ limit, page }, setParams] = useState({ limit: TABLE_ROW_COUNT_DEFAULT, page: 0 });
    const { sort, sortDir, setSorting } = useSorting(['created_at', 'ASC']);
    const [search, setSearch] = useState('');
    const [view, setView] = useState(getStoredView);
    const navigate = useNavigate();

    const handleViewChange = (newView) => {
        setView(newView);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, newView);
        } catch {
            // localStorage may be unavailable (private mode / quota); keep in-memory view
        }
    };
    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadData,
    } = useGetVenues(
        {
            search,
            page,
            pageSize: limit,
            sort,
            sortDir,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    return (
        <PageWithTitle
            title={can(CAPABILITIES.VENUES_MANAGE) ? t('venues.title') : t('venues.singleTitle')}
            inlineHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    wrap="nowrap"
                    justifyContent="flex-end"
                    alignItems="center"
                    key="venue-actions"
                >
                    {!isMobile && (
                        <Grid2 sx={{ mr: 1 }}>
                            <SearchInput
                                search={search}
                                setSearch={setSearch}
                                setParams={setParams}
                                limit={limit}
                            />
                        </Grid2>
                    )}
                    <Grid2 sx={{ mr: 0.5, flexShrink: 0, display: 'flex' }}>
                        <Tooltip title={t('common.cardView')}>
                            <IconButton
                                size="small"
                                onClick={() => handleViewChange('card')}
                                color={view === 'card' ? 'primary' : 'default'}
                            >
                                <ViewModuleIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.tableView')}>
                            <IconButton
                                size="small"
                                onClick={() => handleViewChange('table')}
                                color={view === 'table' ? 'primary' : 'default'}
                            >
                                <TableRowsIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid2>
                    {can(CAPABILITIES.VENUES_MANAGE) && (
                        <Grid2 sx={{ flexShrink: 0 }}>
                            <ButtonGroup variant="contained" size="small">
                                <Tooltip title={t('venues.newVenueTitle')}>
                                    <Button
                                        onClick={() => {
                                            navigate('/venues/new');
                                        }}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip title={t('venues.importTitle')}>
                                    <Button
                                        onClick={() => {
                                            navigate('/venues/import');
                                        }}
                                    >
                                        <SystemUpdateAltIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid2>
                    )}
                </Grid2>,
            ]}
        >
            {isMobile && (
                <Grid2 size={{ xs: 12 }} sx={{ mb: 1 }}>
                    <SearchInput
                        search={search}
                        setSearch={setSearch}
                        setParams={setParams}
                        limit={limit}
                        fullWidth
                    />
                </Grid2>
            )}
            {view === 'card' ? (
                <VenuesCardView
                    rows={data?.venues}
                    limit={limit}
                    page={page}
                    setParams={setParams}
                    total={data?.total}
                    loading={loading}
                    reloadData={reloadData}
                />
            ) : (
                <VenuesTableView
                    rows={data?.venues}
                    limit={limit}
                    page={page}
                    setParams={setParams}
                    total={data?.total}
                    loading={loading}
                    reloadData={reloadData}
                    sort={sort}
                    sortDir={sortDir}
                    setSorting={setSorting}
                />
            )}
        </PageWithTitle>
    );
};

export default VenuesPage;
